import type { ApiFailure, ApiSuccess, ErrorSupportInfo } from '@chefiapro/shared';
import { fallbackSupport, supportReference, supportStore } from '@/lib/error-support';

function readSupport(value: unknown, code: string): ErrorSupportInfo {
  if (!value || typeof value !== 'object') return fallbackSupport(code);
  const item = value as Partial<ErrorSupportInfo>;
  if (
    typeof item.code !== 'string' ||
    !/^[A-Z][A-Z0-9_-]{2,100}$/.test(item.code) ||
    typeof item.title !== 'string' ||
    typeof item.module !== 'string' ||
    typeof item.category !== 'string' ||
    !Array.isArray(item.steps) ||
    !item.steps.every((step) => typeof step === 'string') ||
    !Array.isArray(item.fields)
  )
    return fallbackSupport(code);
  return {
    code: item.code,
    technicalCode: code,
    title: item.title.slice(0, 500),
    module: item.module.slice(0, 150),
    category: item.category.slice(0, 80),
    steps: item.steps.slice(0, 8).map((step) => step.slice(0, 500)),
    fields: item.fields
      .slice(0, 20)
      .filter(
        (field) =>
          field &&
          typeof field.field === 'string' &&
          typeof field.label === 'string' &&
          typeof field.message === 'string' &&
          ['FIELD_REQUIRED', 'FIELD_INVALID'].includes(field.code),
      ),
  };
}

export class ApiError extends Error {
  public readonly support: ErrorSupportInfo;
  public readonly userMessage: string;
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
    support?: ErrorSupportInfo,
  ) {
    const guidance = readSupport(support, code);
    super(
      `${message} [Código: ${guidance.code}]${supportReference(requestId) ? ` · Atendimento: ${requestId}` : ''}`,
    );
    this.name = 'ApiError';
    this.userMessage = message;
    this.support = guidance;
  }
}

function report(error: ApiError, path: string): never {
  // Expected session probes and MFA enrollment/challenges must not expose challenge details.
  if (
    !/\/auth\/(refresh|logout|session)(?:$|\?)/.test(path) &&
    !['MFA_REQUIRED', 'MFA_SETUP_REQUIRED'].includes(error.code)
  ) {
    supportStore.record({
      support: error.support,
      requestId: supportReference(error.requestId),
      status: error.status,
      occurredAt: new Date().toISOString(),
    });
  }
  throw error;
}
export async function safeFetch(url: string, init: RequestInit = {}): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    return report(
      new ApiError(
        0,
        'NETWORK_ERROR',
        'Não foi possível receber a resposta. Confira a conexão e o histórico antes de repetir a operação.',
      ),
      url,
    );
  }
}
export async function parseApiResponse<T>(response: Response, path = ''): Promise<ApiSuccess<T>> {
  if (response.status === 204) return { success: true, data: undefined as T };
  const payload = (await response.json().catch(() => null)) as ApiSuccess<T> | ApiFailure | null;
  if (response.ok && payload?.success === true) return payload;
  const failure = payload as ApiFailure | null;
  const code =
    failure?.error?.code ?? (response.status === 429 ? 'RATE_LIMITED' : 'INVALID_RESPONSE');
  return report(
    new ApiError(
      response.status,
      code,
      failure?.error?.message ??
        'Não foi possível interpretar a resposta. Consulte o histórico antes de tentar novamente.',
      failure?.error?.details,
      failure?.requestId ?? response.headers.get('x-request-id') ?? undefined,
      failure?.error?.support,
    ),
    path,
  );
}
export async function checkDownloadResponse(response: Response, path: string) {
  if (!response.ok) await parseApiResponse(response, path);
}
